import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
} from 'date-fns';

// Causes React to sleep for the delay provided in milliseconds
export const timeout = (delay: number) => {
  return new Promise((res) => setTimeout(res, delay));
};

/**
 * Formats a timestamp into a human readable string depicting time since/until then.
 *
 * @param timestamp - The timestamp provide as a 'Date' type
 * @param extended - Boolean whether or not to condense the string to use abbreviations
 * @param absolute - Returns the positive of the timestamp
 * @param countDown - When true, calculates time until the future timestamp
 * @param floor - When true, returns "0s" if the difference is negative
 * @returns A formatted string representing the time since/until the timestamp
 */
export const timeFrom = (
  timestamp: Date,
  extended = false,
  absolute = true,
  countDown = false,
  floor = false
): string => {
  const now = new Date();
  const getDiff = (fn: (date1: Date, date2: Date) => number) => {
    // For countdown, we want timestamp - now (reversed order)
    const diff = countDown ? fn(timestamp, now) : fn(now, timestamp);

    // If floor is true and diff is negative, return 0
    if (floor && diff < 0) return 0;
    return absolute ? Math.abs(diff) : diff;
  };

  // Return full date if difference is more than a week
  if (getDiff(differenceInWeeks) > 1) {
    return timestamp.toLocaleDateString();
  }

  // Import differenceInSeconds from date-fns
  const differenceInSeconds = (date1: Date, date2: Date) =>
    Math.floor((date1.getTime() - date2.getTime()) / 1000);

  const diffs = [
    {
      value: Math.floor(getDiff(differenceInMonths) / 12),
      unit: 'year',
      abbr: 'y',
    },
    { value: getDiff(differenceInMonths) % 12, unit: 'month', abbr: 'mo' },
    { value: getDiff(differenceInWeeks), unit: 'week', abbr: 'w' },
    { value: getDiff(differenceInDays), unit: 'day', abbr: 'd' },
    { value: getDiff(differenceInHours), unit: 'hour', abbr: 'h' },
    { value: getDiff(differenceInMinutes), unit: 'minute', abbr: 'm' },
    { value: getDiff(differenceInSeconds), unit: 'second', abbr: 's' },
  ];

  for (const { value, unit, abbr } of diffs) {
    if (value !== 0) {
      return extended
        ? `${value} ${unit}${value !== 1 ? 's' : ''}`
        : `${value}${abbr}`;
    }
  }

  return extended ? '0 minutes' : '0m';
};

/**
 * Formats a number of points into a human-readable string representation.
 * - For points >= 999,999, returns the value in millions with 'm' suffix (e.g., "1.2m")
 * - For points > 999, returns the value in thousands with 'k' suffix (e.g., "1.2k")
 * - Otherwise, returns the points as a string
 *
 * @param points - The number of points to format
 * @returns A formatted string representation of the points
 */
export const formatPoints = (points: number): string => {
  if (points >= 999_999) {
    points = points / 1_000_000;
    return `${points.toFixed(1)}m`;
  } else if (points > 999) {
    points = points / 1_000;
    return `${points.toFixed(1)}k`;
  } else {
    return `${points}`;
  }
};
